import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BehaviorSubject, combineLatest, filter, map, tap } from 'rxjs';

import { UserQuery } from '@common/store/user.store';
import { AuthorisationSnapshot } from '@common/types';
import { isEmpty } from '@common/util/util';

@Injectable({
  providedIn: 'root',
})
export class AuthorisationService {
  constructor(private userQuery: UserQuery) {
    this.user$
      .pipe(
        takeUntilDestroyed(),
        filter((user) => !isEmpty(user)),
        tap((user) => {
          this.snapshot.isAdmin = user.roles?.includes('admin') ?? false;
          this.snapshot.isEditor = user.roles?.includes('editor') ?? false;
          this.snapshot.isSupport = user.is_support ?? false;
          this.snapshot.hasWritePermission = this.snapshot.isAdmin || this.snapshot.isEditor || this.snapshot.isSupport;

          this.snapshotReadySubject.next(this.snapshot);
        }),
      )
      .subscribe();
  }

  snapshot = {
    isAdmin: false,
    isEditor: false,
    isSupport: false,
    hasWritePermission: false,
  };

  snapshotReadySubject = new BehaviorSubject<null | AuthorisationSnapshot>(null);
  snapshotReady$ = this.snapshotReadySubject.asObservable().pipe(filter((snapshot) => snapshot !== null));

  private user$ = this.userQuery.user$;

  isAdmin$ = this.user$.pipe(map((user) => user.roles?.includes('admin')));

  isEditor$ = this.user$.pipe(map((user) => user.roles?.includes('editor')));

  isSupport$ = this.user$.pipe(map((user) => user.is_support || user.roles?.includes('support') || false));

  isGuest$ = this.user$.pipe(map((user) => user.roles?.includes('guest')));

  isEndUser$ = this.user$.pipe(map((user) => user.roles?.includes('enduser') || user.roles?.includes('end_user')));

  hasWritePermission$ = combineLatest([this.isAdmin$, this.isEditor$, this.isSupport$]).pipe(
    map(([isAdmin, isEditor, isSupport]) => isAdmin || isEditor || isSupport),
  );
}
